import { NgClass, NgComponentOutlet, NgStyle, NgTemplateOutlet } from '@angular/common';
import { Component, computed, HostBinding, inject, input, TemplateRef, Type, AfterViewInit, OnDestroy, HostListener, ElementRef, viewChild } from '@angular/core';
import { ModalContent, ModalEvent, ModalOptions } from './model';
import { Subject } from 'rxjs';
import { ModalService } from './modal.service';
import { SvgIconComponent } from "../svg-icon/svg-icon.component";

@Component({
  selector: 'app-modal',
  imports: [NgComponentOutlet, NgTemplateOutlet, NgClass, NgStyle, SvgIconComponent],
  templateUrl: './modal.component.html',
  styleUrl: './modal.component.css'
})
export class ModalComponent implements AfterViewInit, OnDestroy {

  id = input.required<number>()
  content = input.required<ModalContent>()
  options = input.required<ModalOptions>()
  data = input<Record<string, unknown>>()

  private modal = inject(ModalService)
  private element = inject<ElementRef<HTMLElement>>(ElementRef)
  private eventsSubject = new Subject<ModalEvent>()
  readonly events$ = this.eventsSubject.asObservable()

  protected componentOutlet = viewChild<NgComponentOutlet>(NgComponentOutlet)

  protected contentComponent = computed(() => {
    const content = this.content()
    if (content instanceof Type) {
      return content
    }
    return undefined
  })

  protected contentTemplate = computed(() => {
    const content = this.content()
    if (content instanceof TemplateRef) {
      return content
    }
    return undefined
  })

  protected contentString = computed(() => {
    const content = this.content()
    if (typeof(content) == 'string') {
      return content
    }
    return undefined
  })

  protected cssClass = computed(() => undefined)
  protected cssStyle = computed(() => this.resolveCssStyle(this.options()))

  private _hostCssClass = computed(() => this.options().cssClass)
  private _hostCssStyle = computed(() => undefined)

  ngAfterViewInit(): void {
    this.modal.registerModal(this)
    this.eventsSubject.next({show: true})
  }

  ngOnDestroy(): void {
    this.eventsSubject.next({show: false})
    this.eventsSubject.complete()
  }

  @HostBinding('class') get hostCssClass() {
    return this._hostCssClass()
  }

  @HostBinding('style') get hostCssStyle() {
    return this._hostCssStyle()
  }

  @HostListener('click', ['$event.target'])
  onClick(target: EventTarget | null) {
    if (this.options().destroyOnLayoutClick && target instanceof HTMLElement && this.element.nativeElement === target) {
      this.modal.destroy(this)
    }
  }

  protected close() {
    this.modal.destroy(this)
  }

  getComponentInstance<T>(): T | undefined {
    return this.componentOutlet()?.componentInstance
  }

  private resolveCssStyle(options: ModalOptions | undefined) {
    const res: Record<string, string> = {}

    if (typeof(options?.backgroundColor) == 'string') {
      res['backgroundColor'] = options.backgroundColor
    }

    if (typeof(options?.width) == 'number') {
      res['width'] = `${options.width}px`
    }
    if (typeof(options?.height) == 'number') {
      res['height'] = `${options.height}px`
    }

    if (typeof(options?.minWidth) == 'number') {
      res['minWidth'] = `${options.minWidth}px`
    }
    if (typeof(options?.minHeight) == 'number') {
      res['minHeight'] = `${options.minHeight}px`
    }

    if (typeof(options?.maxWidth) == 'number') {
      res['maxWidth'] = `${options.maxWidth}px`
    }
    if (typeof(options?.maxHeight) == 'number') {
      res['maxHeight'] = `${options.maxHeight}px`
    }

    if (typeof(options?.padding) == 'string') {
      res['padding'] = options.padding
    }
    else if (typeof(options?.padding) == 'number') {
      res['padding'] = `${options.padding}px`
    }
    else if (options && Array.isArray(options.padding)) {
      res['padding'] = options.padding.map(n => `${n}px`).join(' ')
    }

    return res
  }

}
